import { type ReactNode, useState } from "react";

import {
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";

import { Button } from "@/components/ui/button";
import { DayCard } from "@/pages/tina/festivals/components/page/day-card";
import { RemovedDays } from "@/pages/tina/festivals/components/page/removed-days";
import { ChipFace, Tray } from "@/pages/tina/festivals/components/page/tray";
import {
  type PageForm,
  type PageFormAction,
  dayNumbers,
} from "@/pages/tina/festivals/lib/page-form";
import {
  trayDragName,
  trayDropTarget,
} from "@/pages/tina/festivals/lib/page-request";
import type { FestivalPageRead } from "@/types/tina-festival-page";

interface DaysSectionProps {
  form: PageForm;
  dispatch: (action: PageFormAction) => void;
  page: FestivalPageRead;
  /** The page's problem keys (shape problems and Tina's); a day shows the ones naming its date. */
  problems: string[];
  editable: boolean;
  /** The suggestions about one day, by the day's form key. */
  suggestionsFor?: (dayKey: string) => ReactNode;
  /** Shown after the days: the suggestions of days Tina found. */
  afterDays?: ReactNode;
}

/** The days of a day-ticket festival: the tray of artists with no day, then one card per day. */
export function DaysSection({
  form,
  dispatch,
  page,
  problems,
  editable,
  suggestionsFor,
  afterDays,
}: DaysSectionProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );
  const numbers = dayNumbers(form.days);
  const ordered = [...form.days].sort((a, b) =>
    a.day === b.day ? 0 : a.day < b.day ? -1 : 1,
  );

  // The chip being dragged, drawn under the pointer by the overlay while its tray copy stays dimmed.
  const [dragging, setDragging] = useState<string | null>(null);
  const onDragStart = (event: DragStartEvent) =>
    setDragging(trayDragName(event.active.id));
  const onDragEnd = (event: DragEndEvent) => {
    setDragging(null);
    const target = trayDropTarget(event);
    if (target) dispatch({ type: "place", ...target });
  };

  return (
    <section className="flex flex-col gap-3 rounded-lg border p-4">
      <h2 className="text-lg font-semibold">Days, venues and lineups</h2>
      <DndContext
        sensors={sensors}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragCancel={() => setDragging(null)}
      >
        <Tray
          names={form.tray}
          placedCount={Math.max(0, page.tray.length - form.tray.length)}
          dayLabels={ordered.map((d) => ({
            key: d.key,
            number: numbers.get(d.key) ?? 0,
          }))}
          editable={editable}
          dispatch={dispatch}
        />
        <div className="grid grid-cols-1 gap-3 min-[560px]:grid-cols-2">
          {ordered.map((day) => (
            <DayCard
              key={day.key}
              day={day}
              number={numbers.get(day.key) ?? 0}
              problems={problems}
              editable={editable}
              dispatch={dispatch}
              box={page.days.find((d) => d.day === day.loadedDay)}
              suggestions={suggestionsFor?.(day.key)}
            />
          ))}
        </div>
        <DragOverlay dropAnimation={null}>
          {dragging !== null ? <ChipFace name={dragging} lifted /> : null}
        </DragOverlay>
      </DndContext>
      <RemovedDays form={form} dispatch={dispatch} page={page} />
      {afterDays}
      {editable ? (
        <Button
          type="button"
          variant="outline"
          className="self-start"
          onClick={() => dispatch({ type: "add_day" })}
        >
          + Add a day
        </Button>
      ) : null}
    </section>
  );
}
